import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import type { BranchTarget } from '@/lib/native-desktop';
import type { DesktopShellState } from './store';

/**
 * What the header's branch chip (design D45) can do, as the shell's dialog layer registers it:
 * the switch the dialogs confirm, and the creation of a branch. A shell without them (a
 * preview, an embedded chat) leaves the chip's actions off.
 */
export type BranchActions = {
  switchBranch: ((target: BranchTarget) => void | Promise<void>) | null;
  createBranch: (() => void) | null;
};

export const branchActionsStore = createStore<BranchActions>(() => ({
  switchBranch: null,
  createBranch: null,
}));

export function useBranchActions(): BranchActions {
  return useStore(branchActionsStore);
}

/**
 * How a branch picked from the chip is switched to: in the worktree that holds it already, after
 * a confirmation when the checkout has changes, else at once.
 */
export function branchSwitchStep(
  state: Pick<DesktopShellState, 'gitStatus' | 'workspace'>,
  target: BranchTarget,
): 'open-worktree' | 'confirm' | 'switch' | 'none' {
  const { gitStatus, workspace } = state;
  if (workspace === null) return 'none';
  if (!target.remote && target.branch === (gitStatus?.branch ?? '')) return 'none';
  if (gitStatus?.worktrees.some((tree) => !tree.isCurrent && tree.branch === target.branch))
    return 'open-worktree';
  return gitStatus?.hasChanges === true ? 'confirm' : 'switch';
}
